import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import chapterActions from 'src/redux/actions/chapterActions';
import chapterSelectors from 'src/redux/selectors/chapterSelectors';
import { Tooltip } from 'antd';
import Icon, { ZoomInOutlined, ZoomOutOutlined, ColumnHeightOutlined, ColumnWidthOutlined } from '@ant-design/icons';
import moonIcon from 'src/assets/icons/moon.svg';
import sunIcon from 'src/assets/icons/sun.svg';
import { StyledActionBar, StyledSwitch } from './styles';

const PreviewChapter = () => {
  const dispatch = useDispatch();
  const params = new Proxy(new URLSearchParams(window.location.search), {
    get: (searchParams, prop) => searchParams.get(prop),
  });
  const { comicName, comicId, chapterName, chapterId } = params;
  const [images, setImages] = useState([]);
  const fullWidth = useSelector(chapterSelectors.fullWidth);
  const scrollHorizontal = useSelector(chapterSelectors.scrollHorizontal);
  const darkMode = useSelector(chapterSelectors.darkMode);
  const [actionBarVisible, setActionBarVisible] = useState(!fullWidth);

  const toggleFullWidth = () => {
    dispatch(chapterActions.toggleFullWidth(!fullWidth));
  };

  const toggleScrollHorizontal = () => {
    dispatch(chapterActions.toggleScrollHorizontal(!scrollHorizontal));
  };

  const toggleDarkMode = () => {
    dispatch(chapterActions.toggleDarkMode(!darkMode));
  };

  const onEscPressed = useCallback((event) => {
    if (event.key === "Escape") {
      dispatch(chapterActions.toggleFullWidth(false));
      dispatch(chapterActions.toggleScrollHorizontal(false));
    }
  }, []);

  useEffect(() => {
    document.title = `${comicName} | ${chapterName}`;
    dispatch(chapterActions.fetchChapter({ comicId, chapterId }, (data) => setImages(data)));

    // exit full width mode if user press esc
    document.addEventListener("keydown", onEscPressed, false);
    return () => {
      document.removeEventListener("keydown", onEscPressed, false);
    };
  }, []);

  useEffect(() => {
    setActionBarVisible(!fullWidth || !scrollHorizontal);
  }, [fullWidth, scrollHorizontal]);

  const MoonComponent = () => <img alt='' src={moonIcon} />;
  const SunComponent = () => <img alt='' src={sunIcon} />;

  return (
    <div style={{ ...styles.container, backgroundColor: darkMode ? '#121212' : 'white', flexDirection: scrollHorizontal ? 'row' : 'column' }}>
      <StyledActionBar
        style={{
          ...styles.actionBar,
          ...(!actionBarVisible && { opacity: 0, transition: 'all 3s', transitionDelay: '3s' })
        }}
        onMouseLeave={() => (fullWidth || scrollHorizontal) && setActionBarVisible(false)}
        onMouseEnter={() => setActionBarVisible(true)}
      >
        <Tooltip title={fullWidth ? "Exit full width" : "Full width"}>
          {fullWidth
            ? <ZoomOutOutlined style={styles.actionButton} onClick={toggleFullWidth} />
            : <ZoomInOutlined style={styles.actionButton} onClick={toggleFullWidth} />
          }
        </Tooltip>
        <Tooltip title={scrollHorizontal ? "Scroll vertical" : "Scroll horizontal"}>
          {scrollHorizontal
            ? < ColumnHeightOutlined style={styles.actionButton} onClick={toggleScrollHorizontal} />
            : <ColumnWidthOutlined style={styles.actionButton} onClick={toggleScrollHorizontal} />
          }
        </Tooltip>
        <Tooltip title={darkMode ? "Light mode" : "Dark mode"}>
          <StyledSwitch
            checkedChildren={<Icon component={MoonComponent} />}
            checkedColor='#513C7F'
            unCheckedChildren={<Icon component={SunComponent} />}
            unCheckedColor='#c4e6ff'
            style={{ marginLeft: 10, marginRight: 10 }}
            onClick={toggleDarkMode}
          />
        </Tooltip>
      </StyledActionBar>
      {images.map((image =>
        <img key={image.name} alt={image.name} src={image.url} style={{ width: fullWidth ? '100%' : 'auto', height: fullWidth ? 'auto' : '100%', objectFit: 'contain' }} />
      ))}
    </div>
  )
}

const styles = {
  container: {
    display: 'flex',
    flex: 1,
    overflow: 'auto',
  },
  actionBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'fixed',
    top: 10,
    right: 25,
    backgroundColor: '#e5e5e5',
    padding: 5,
    borderRadius: 5,
  },
  actionButton: {
    fontSize: 18,
    paddingLeft: 10,
    paddingRight: 10,
    cursor: 'pointer'
  }
};

export default PreviewChapter;